/** @jsx jsx */
import React, { useState, useEffect } from "react";
import { jsx, css } from "@emotion/core";
import { Redirect } from "react-router";
import { Formik, Field } from "formik";

import _find from "lodash/find";
import _get from "lodash/get";

import Input, {
    FormikInput,
    PasswordInput,
    SearchableDropdown,
} from "../../components/Input";
import PrimaryCTA from "../../components/PrimaryCTA";
import SecondaryCTA from "../../components/SecondaryCTA";
import CTAGroup from "../../components/CTAGroup";

import Layout from "../../components/Layout";
import Header from "../../components/Header";
import TabSelect from "../../components/TabSelect";
import Form from "../../components/Form";
import FormField, { FormFieldRow } from "../../components/FormField";
import { Checkbox } from "../../components/CheckboxGroup";
import Label from "../../components/Label";
import ValidationError from "../../components/ValidationError";

import {
    fetchUniversityNames,
    fetchEmailDomains,
    submitDetails,
} from "./apiCalls";

import validator from "./validator";
import { validateEmailPrefixFormat } from "./validator";

const initialValues = {
    firstName: "",
    lastName: "",
    universityId: "",
    universityName: "",
    emailPrefix: "",
    emailPrefixConfirmation: "",
    emailExtension: "",
    personalEmail: "",
    personalEmailConfirmation: "",
    hasAccessToUniEmail: true,
    password: "",
};

const privacyPolicyStyle = css`
    margin-top: 16px;
`;

const PrivacyPolicy = () => (
    <p css={privacyPolicyStyle}>
        By signing up you agree to our{" "}
        <a href="https://www.mytutor.co.uk/privacy">privacy policy</a>
    </p>
);

const SignupForm = () => {
    const [universityNames, setUniversityNames] = useState([]);
    const [emailDomains, setEmailDomains] = useState([]);
    const [selectedTabTitle, setSelectedTabTitle] = useState(
        "Currently studying"
    );
    const [formSubmissionComplete, setFormSubmissionComplete] = useState(false);
    const [formSubmissionSuccessful, setFormSubmissionSuccessful] = useState(
        false
    );

    const [submittedEmailAddress, setSubmittedEmailAddress] = useState("");
    const [hasAccessToUniEmail, setHasAccessToUniEmail] = useState("");
    const [globalErrorMessage, setGlobalErrorMessage] = useState("");

    useEffect(() => {
        fetchUniversityNames(setUniversityNames);
    }, []);

    return formSubmissionComplete ? (
        <Redirect
            push
            to={{
                pathname: `/signup/complete/${
                    formSubmissionSuccessful ? "success" : "error"
                }`,
                state: {
                    emailAddress: submittedEmailAddress,
                    hasAccessToUniEmail: hasAccessToUniEmail,
                },
            }}
        />
    ) : (
        <Formik
            initialValues={initialValues}
            validate={validator(selectedTabTitle)}
            validateOnBlur={false}
            validateOnChange={false}
            onSubmit={(values, actions) => {
                submitDetails({
                    values,
                    studyingStatus: selectedTabTitle,
                }).then(response => {
                    actions.setSubmitting(false);
                    if (!response.error) {
                        setSubmittedEmailAddress(_get(response, "data"));
                        setHasAccessToUniEmail(values.hasAccessToUniEmail);
                        setFormSubmissionSuccessful(true);
                        setFormSubmissionComplete(true);
                    } else if (response.error && response.status == 400) {
                        setGlobalErrorMessage(_get(response, "data"));
                        setFormSubmissionSuccessful(false);
                        setFormSubmissionComplete(false);
                    } else {
                        setFormSubmissionComplete(true);
                    }

                    localStorage.studyingStatus = selectedTabTitle;
                });
            }}
            render={({ isSubmitting, ...props }) => (
                <Form onSubmit={props.handleSubmit}>
                    <h1>Sign up</h1>
                    <TabSelect
                        tabTitles={["Currently studying", "Recently graduated"]}
                        selectTab={title => setSelectedTabTitle(title)}
                        selectedTabTitle={selectedTabTitle}
                    />
                    <FormField>
                        <Label htmlFor="firstName">First name</Label>
                        <Field
                            id="firstName"
                            name="firstName"
                            autoComplete="given-name"
                            component={FormikInput}
                        />
                    </FormField>
                    <FormField>
                        <Label htmlFor="lastName">Last name</Label>
                        <Field
                            id="lastName"
                            name="lastName"
                            autoComplete="family-name"
                            component={FormikInput}
                        />
                    </FormField>
                    <FormField>
                        <Label htmlFor="personalEmail">Personal email</Label>
                        <Input
                            type="text"
                            onChange={props.handleChange}
                            onBlur={props.handleBlur}
                            value={props.values.personalEmail}
                            name="personalEmail"
                            id="personalEmail"
                            autoComplete="email"
                            error={
                                props.touched.personalEmail &&
                                props.errors.personalEmail
                            }
                        />
                        {props.touched.personalEmail &&
                            props.errors.personalEmail && (
                                <ValidationError>
                                    {props.errors.personalEmail}
                                </ValidationError>
                            )}
                    </FormField>
                    <FormField>
                        <Label htmlFor="personalEmailConfirmation">
                            Confirm email
                        </Label>
                        <Input
                            type="text"
                            onChange={props.handleChange}
                            onBlur={props.handleBlur}
                            value={props.values.personalEmailConfirmation}
                            name="personalEmailConfirmation"
                            id="personalEmailConfirmation"
                            error={
                                props.touched.personalEmailConfirmation &&
                                props.errors.personalEmailConfirmation
                            }
                        />
                        {props.touched.personalEmailConfirmation &&
                            props.errors.personalEmailConfirmation && (
                                <ValidationError>
                                    {props.errors.personalEmailConfirmation}
                                </ValidationError>
                            )}
                    </FormField>

                    <FormField>
                        <Label htmlFor="university">University</Label>
                        <SearchableDropdown
                            inputId="university-name"
                            value={[
                                {
                                    label: props.values.universityName,
                                    value: props.values.universityName,
                                },
                            ]}
                            options={universityNames.map(uni => ({
                                key: uni,
                                value: uni,
                                label: uni,
                            }))}
                            error={
                                props.touched.universityName &&
                                props.errors.universityName
                            }
                            onChange={({ value }) => {
                                if (value === "Other...") {
                                    return window.location.assign(
                                        "https://www.mytutor.co.uk/tutors/application.html"
                                    );
                                }
                                fetchEmailDomains(value)({
                                    setFieldValue: props.setFieldValue,
                                    setEmailDomains,
                                });

                                return props.setFieldValue(
                                    "universityName",
                                    value
                                );
                            }}
                        />
                        {props.touched.universityName &&
                            props.errors.universityName && (
                                <ValidationError>
                                    {props.errors.universityName}
                                </ValidationError>
                            )}
                    </FormField>
                    {(selectedTabTitle === "Currently studying" ||
                        props.values.hasAccessToUniEmail) && (
                        <>
                            <FormField>
                                <FormFieldRow>
                                    <FormField>
                                        <Label htmlFor="emailPrefix">
                                            Uni email prefix
                                        </Label>
                                        <Input
                                            type="text"
                                            value={props.values.emailPrefix}
                                            name="emailPrefix"
                                            id="emailPrefix"
                                            data-testid="emailPrefix"
                                            onChange={e => {
                                                props.errors.emailPrefix = validateEmailPrefixFormat(
                                                    e.target.value,
                                                    emailDomains.length > 1
                                                );
                                                return props.handleChange(e);
                                            }}
                                            onBlur={props.handleBlur}
                                            error={props.errors.emailPrefix}
                                        />
                                        {props.errors.emailPrefix && (
                                            <ValidationError>
                                                {props.errors.emailPrefix}
                                            </ValidationError>
                                        )}
                                    </FormField>
                                    <FormField>
                                        <Label htmlFor="emailExtension">
                                            Uni email domain
                                        </Label>
                                        <SearchableDropdown
                                            isDisabled={emailDomains.length < 2}
                                            value={{
                                                label:
                                                    props.values.emailExtension,
                                                value:
                                                    props.values.emailExtension,
                                            }}
                                            options={emailDomains.map(
                                                emailDomain => ({
                                                    key: emailDomain.id,
                                                    value:
                                                        emailDomain.emailExtension,
                                                    label:
                                                        emailDomain.emailExtension,
                                                })
                                            )}
                                            error={
                                                props.touched.emailExtension &&
                                                props.errors.emailExtension
                                            }
                                            onChange={({ value }) => {
                                                const universityId = _get(
                                                    _find(emailDomains, [
                                                        "emailExtension",
                                                        value,
                                                    ]),
                                                    "id"
                                                );
                                                props.setFieldValue(
                                                    "universityId",
                                                    universityId
                                                );
                                                return props.setFieldValue(
                                                    "emailExtension",
                                                    value
                                                );
                                            }}
                                            inputId="emailExtension"
                                        />
                                        {props.touched.emailExtension &&
                                            props.errors.emailExtension && (
                                                <ValidationError>
                                                    {
                                                        props.errors
                                                            .emailExtension
                                                    }
                                                </ValidationError>
                                            )}
                                    </FormField>
                                </FormFieldRow>
                            </FormField>
                            <FormField>
                                <FormFieldRow>
                                    <FormField>
                                        <Label htmlFor="emailPrefixConfirmation">
                                            Confirm email
                                        </Label>
                                        <Input
                                            type="text"
                                            value={
                                                props.values
                                                    .emailPemailPrefixConfirmationrefix
                                            }
                                            name="emailPrefixConfirmation"
                                            id="emailPrefixConfirmation"
                                            data-testid="emailPrefixConfirmation"
                                            onChange={e => {
                                                props.errors.emailPrefixConfirmation = validateEmailPrefixFormat(
                                                    e.target.value,
                                                    emailDomains.length > 1
                                                );
                                                return props.handleChange(e);
                                            }}
                                            onBlur={props.handleBlur}
                                            error={
                                                props.errors
                                                    .emailPrefixConfirmation
                                            }
                                        />
                                        {props.errors
                                            .emailPrefixConfirmation && (
                                            <ValidationError>
                                                {
                                                    props.errors
                                                        .emailPrefixConfirmation
                                                }
                                            </ValidationError>
                                        )}
                                    </FormField>
                                    <FormField disabled>
                                        <Label htmlFor="emailExtension">
                                            Uni email domain
                                        </Label>
                                        <SearchableDropdown
                                            isDisabled={emailDomains.length < 2}
                                            value={{
                                                label:
                                                    props.values.emailExtension,
                                                value:
                                                    props.values.emailExtension,
                                            }}
                                            options={emailDomains.map(
                                                emailDomain => ({
                                                    key: emailDomain.id,
                                                    value:
                                                        emailDomain.emailExtension,
                                                    label:
                                                        emailDomain.emailExtension,
                                                })
                                            )}
                                            error={
                                                props.touched.emailExtension &&
                                                props.errors.emailExtension
                                            }
                                            onChange={({ value }) => {
                                                const universityId = _get(
                                                    _find(emailDomains, [
                                                        "emailExtension",
                                                        value,
                                                    ]),
                                                    "id"
                                                );
                                                props.setFieldValue(
                                                    "universityId",
                                                    universityId
                                                );
                                                return props.setFieldValue(
                                                    "emailExtension",
                                                    value
                                                );
                                            }}
                                            inputId="emailExtension"
                                        />
                                        {props.touched.emailExtension &&
                                            props.errors.emailExtension && (
                                                <ValidationError>
                                                    {
                                                        props.errors
                                                            .emailExtension
                                                    }
                                                </ValidationError>
                                            )}
                                    </FormField>
                                </FormFieldRow>
                            </FormField>
                        </>
                    )}
                    {selectedTabTitle === "Recently graduated" && (
                        <Checkbox
                            checked={!props.values.hasAccessToUniEmail}
                            label="I don't have access to my uni email"
                            onChange={() => {
                                return props.setFieldValue(
                                    "hasAccessToUniEmail",
                                    !props.values.hasAccessToUniEmail
                                );
                            }}
                        />
                    )}
                    <FormField>
                        <Label htmlFor="password">Password</Label>
                        <PasswordInput
                            autoComplete="new-password"
                            onChange={props.handleChange}
                            onBlur={props.handleBlur}
                            value={props.values.password}
                            name="password"
                            id="password"
                            error={
                                props.touched.password && props.errors.password
                            }
                        />
                        {props.touched.password && props.errors.password && (
                            <ValidationError>
                                {props.errors.password}
                            </ValidationError>
                        )}
                    </FormField>
                    <CTAGroup>
                        {globalErrorMessage && (
                            <ValidationError>
                                {globalErrorMessage}
                            </ValidationError>
                        )}
                        <PrimaryCTA
                            onClick={props.onSubmit}
                            type="submit"
                            disabled={isSubmitting}
                        >
                            Sign up
                        </PrimaryCTA>
                        <PrivacyPolicy />
                        <SecondaryCTA href="https://www.mytutor.co.uk/login.html">
                            Already a MyTutor tutor?
                        </SecondaryCTA>
                    </CTAGroup>
                </Form>
            )}
        />
    );
};

export default () => (
    <>
        <Header />
        <Layout>
            <SignupForm />
        </Layout>
    </>
);
